import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import Comments from '../components/Comments';
import ToggleSwitch from '../components/ToggleSwitch';
import ReelImage from '../components/ReelImage';
import ReelModal from '../components/ReelModal';
import ReelTableView from '../components/ReelTableView';
import { X } from 'lucide-react';

// 릴 비교 모달 컴포넌트 (크기별 상세 비교)
const ReelCompareModal = React.memo(({ compareItems, onClose, onRemoveFromCompare }) => {
  if (!compareItems || compareItems.length === 0) return null;

  // 각 속성에 대해 서로 다른 값이 있는지 확인하는 함수
  const hasVariation = (property, getValue) => {
    const values = compareItems.map(getValue);
    return new Set(values).size > 1;
  };

  // 값이 다른 경우 강조 클래스를 반환하는 함수
  const getHighlightClass = (property, getValue) => {
    return hasVariation(property, getValue) ? 'highlight-different' : '';
  };

  // 모든 항목이 '정보 없음'인지 확인하는 함수
  const isAllNoInfo = (getValue) => {
    return compareItems.every(item => {
      const value = getValue(item);
      return !value || value === '정보 없음' || value === '' || value === 0;
    });
  };

  // 행을 숨길지 결정하는 함수
  const getRowClass = (getValue) => {
    return isAllNoInfo(getValue) ? 'compare-row-hidden' : '';
  };

  return (
    <div className="compare-modal-overlay" onClick={onClose}>
      <div className="compare-modal" onClick={(e) => e.stopPropagation()}>
        <div className="compare-modal-header">
          <h2>릴 크기별 상세 비교 ({compareItems.length}개)</h2>
          <button 
            className="modal-close-btn"
            onClick={onClose}
          >
            <X className="h-3 w-3" color="#6c757d" />
          </button>
        </div>
        
        <div className="compare-content">
          <div className="compare-table-container">
            <table className="compare-table">
              <thead>
                <tr>
                  <th className="spec-row-label">항목</th>
                  {compareItems.map((item, index) => (
                    <th key={`${item.reelId}-${item.sizeName}-${index}`}>
                      <div className="compare-header-item">
                        <button 
                          className="compare-remove-btn"
                          onClick={() => onRemoveFromCompare(item.reelId, item.sizeName)}
                          title="비교에서 제거"
                        >
                          <X className="h-3 w-3" color="white" />
                        </button>
                        <ReelImage 
                          filename={item.reel.filename}
                          alt={item.reel.name}
                          className="compare-reel-image"
                          onError={() => {
                            console.warn('Failed to load compare image:', item.reel.filename);
                          }}
                        />
                        <div className="compare-reel-name">{item.reel.name}</div>
                        <div className="compare-reel-type">{item.reel.manufacturer} - {item.sizeName}</div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="spec-row-label">릴 타입</td>
                  {compareItems.map((item, index) => (
                    <td key={`type-${index}`} className={getHighlightClass('type', (item) => item.reel.reel_type)}>
                      {item.reel.reel_type}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size.size)}>
                  <td className="spec-row-label">크기</td>
                  {compareItems.map((item, index) => (
                    <td key={`size-${index}`} className={getHighlightClass('size', (item) => item.size.size)}>
                      {item.size.size || '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="spec-row-label">해수 저항</td>
                  {compareItems.map((item, index) => (
                    <td key={`sea-${index}`} className={getHighlightClass('sea', (item) => item.size.issea)}>
                      {item.size.issea ? '💧' : '❌'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size.test_value)}>
                  <td className="spec-row-label">테스트</td>
                  {compareItems.map((item, index) => (
                    <td key={`test-${index}`} className={getHighlightClass('test', (item) => item.size.test_value)}>
                      <div>{item.size.test_value || '정보 없음'}</div>
                      {item.size.test_value_mod1 && <div className="mod-info">개조1: {item.size.test_value_mod1}</div>}
                      {item.size.test_value_mod2 && <div className="mod-info">개조2: {item.size.test_value_mod2}</div>}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size.ratio_value)}>
                  <td className="spec-row-label">기어비</td>
                  {compareItems.map((item, index) => (
                    <td key={`ratio-${index}`} className={getHighlightClass('ratio', (item) => item.size.ratio_value)}>
                      <div>{item.size.ratio_value || '정보 없음'}</div>
                      {item.size.ratio_value_mod1 && <div className="mod-info">개조1: {item.size.ratio_value_mod1}</div>}
                      {item.size.ratio_value_mod2 && <div className="mod-info">개조2: {item.size.ratio_value_mod2}</div>}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size.capacity_value)}>
                  <td className="spec-row-label">권사량</td>
                  {compareItems.map((item, index) => (
                    <td key={`capacity-${index}`} className={getHighlightClass('capacity', (item) => item.size.capacity_value)}>
                      <div>{item.size.capacity_value || '정보 없음'}</div>
                      {item.size.capacity_value_mod1 && <div className="mod-info">개조1: {item.size.capacity_value_mod1}</div>}
                      {item.size.capacity_value_mod2 && <div className="mod-info">개조2: {item.size.capacity_value_mod2}</div>}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size.speed_value)}>
                  <td className="spec-row-label">감는 속도</td>
                  {compareItems.map((item, index) => (
                    <td key={`speed-${index}`} className={getHighlightClass('speed', (item) => item.size.speed_value)}>
                      <div>{item.size.speed_value || '정보 없음'}</div>
                      {item.size.speed_value_mod1 && <div className="mod-info">개조1: {item.size.speed_value_mod1}</div>}
                      {item.size.speed_value_mod2 && <div className="mod-info">개조2: {item.size.speed_value_mod2}</div>}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size.drag_max_value)}>
                  <td className="spec-row-label">최대 저항</td>
                  {compareItems.map((item, index) => (
                    <td key={`drag-${index}`} className={getHighlightClass('drag', (item) => item.size.drag_max_value)}>
                      <div>{item.size.drag_max_value || '정보 없음'}</div>
                      {item.size.drag_max_value_mod1 && <div className="mod-info">개조1: {item.size.drag_max_value_mod1}</div>}
                      {item.size.drag_max_value_mod2 && <div className="mod-info">개조2: {item.size.drag_max_value_mod2}</div>}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size.engine_power)}>
                  <td className="spec-row-label">엔진 파워</td>
                  {compareItems.map((item, index) => (
                    <td key={`engine-${index}`} className={getHighlightClass('engine', (item) => item.size.engine_power)}>
                      {item.size.engine_power || '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size.internal_load_value)}>
                  <td className="spec-row-label">내부 하중</td>
                  {compareItems.map((item, index) => (
                    <td key={`load-${index}`} className={getHighlightClass('load', (item) => item.size.internal_load_value)}>
                      <div>{item.size.internal_load_value || '정보 없음'}</div>
                      {item.size.internal_load_value_mod1 && <div className="mod-info">개조1: {item.size.internal_load_value_mod1}</div>}
                      {item.size.internal_load_value_mod2 && <div className="mod-info">개조2: {item.size.internal_load_value_mod2}</div>}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size.req_levels)}>
                  <td className="spec-row-label">필요 레벨</td>
                  {compareItems.map((item, index) => (
                    <td key={`level-${index}`} className={getHighlightClass('level', (item) => item.size.req_levels)}>
                      {item.size.req_levels || '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size.price_silver)}>
                  <td className="spec-row-label">가격 (실버)</td>
                  {compareItems.map((item, index) => (
                    <td key={`price-silver-${index}`} className={getHighlightClass('pricesilver', (item) => item.size.price_silver)}>
                      {item.size.price_silver ? `${item.size.price_silver.toLocaleString()} 실버` : '정보 없음'}
                    </td>
                  ))}
                </tr>
                <tr className={getRowClass((item) => item.size.price_gold)}>
                  <td className="spec-row-label">가격 (골드)</td>
                  {compareItems.map((item, index) => (
                    <td key={`price-gold-${index}`} className={getHighlightClass('pricegold', (item) => item.size.price_gold)}>
                      {item.size.price_gold ? `${item.size.price_gold} 골드` : '정보 없음'}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
});

// 릴 타일 컴포넌트 - 리렌더링 최적화를 위해 분리
const ReelTile = React.memo(({ reel, onReelClick }) => {
    const handleClick = useCallback(() => {
        onReelClick(reel.id);
    }, [reel.id, onReelClick]);

    return (
        <div 
            className="reel-tile"
            onClick={handleClick}
        >
            <ReelImage 
                filename={reel.filename}
                alt={reel.name}
                className="reel-tile-image"
                onError={() => {
                    console.warn('Failed to load tile image:', reel.filename);
                }}
            />
            <div className="reel-tile-info">
                <div className="reel-tile-type">{reel.reel_type}</div>
                <div className="reel-tile-manufacturer">{reel.manufacturer}</div>
                <div className="reel-tile-name">{reel.name}</div>
                <div className="reel-tile-specs">
                    <div className="spec-item">
                        <span className="spec-label">해수저항:</span>
                        <span className="spec-value">
                            {reel.sea_compatibility ? '💧' : '❌'}
                        </span>
                    </div>
                    <div className="spec-item">
                        <span className="spec-label">크기:</span>
                        <span className="spec-value">
                            {reel.min_size && reel.max_size 
                                ? `${reel.min_size} ~ ${reel.max_size}`
                                : '정보 없음'}
                        </span>
                    </div>
                    <div className="spec-item">
                        <span className="spec-label">기어비:</span>
                        <span className="spec-value">
                            {reel.min_ratio && reel.max_ratio 
                                ? `${reel.min_ratio}:1 ~ ${reel.max_ratio}:1`
                                : '정보 없음'}
                        </span>
                    </div>
                    <div className="spec-item">
                        <span className="spec-label">최대저항:</span>
                        <span className="spec-value">
                            {reel.min_drag && reel.max_drag 
                                ? `${reel.min_drag} kg ~ ${reel.max_drag} kg`
                                : '정보 없음'}
                        </span>
                    </div>
                    <div className="spec-item price">
                        <span className="spec-label">가격:</span>
                        <span className="spec-value">
                            {reel.price_silver && (
                                <span className="price-silver">
                                    최소 {reel.price_silver.toLocaleString()} 실버
                                </span>
                            )}
                            {reel.price_gold && (
                                <span className="price-gold">
                                    최소 {reel.price_gold.toLocaleString()} 골드
                                </span>
                            )}
                            {!reel.price_silver && !reel.price_gold && '정보 없음'}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
});

const Page1 = () => {
    const [reels, setReels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedType, setSelectedType] = useState('all');
    const [selectedReel, setSelectedReel] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('name');
    const [buttonLayout, setButtonLayout] = useState([]);
    const [searchPlaceholder, setSearchPlaceholder] = useState('릴 이름, 제조사, 타입으로 검색...');
    
    // 비교 기능을 위한 state
    const [compareList, setCompareList] = useState([]);
    const [showCompareModal, setShowCompareModal] = useState(false);
    const [viewMode, setViewMode] = useState(() => {
        // 로컬 스토리지에서 마지막 뷰 모드를 가져오기
        try {
            const savedViewMode = localStorage.getItem('reelsViewMode');
            return savedViewMode === 'table' ? 'table' : 'grid';
        } catch (error) {
            console.warn('로컬 스토리지에서 뷰 모드를 가져오는데 실패했습니다:', error);
            return 'grid';
        }
    });
    const [reelSizes, setReelSizes] = useState([]); // 릴 크기 데이터
    
    // 필터링 관련 state
    const [showFilters, setShowFilters] = useState(false);
    // 🚀 성능 최적화: 필터 상태를 하나의 객체로 통합하여 단일 렌더링 보장
    const [filterState, setFilterState] = useState({
        manufacturers: [],
        sizes: [],
        loaded: false,
        loading: false
    });
    const [filters, setFilters] = useState({
        manufacturer: 'all',
        size: 'all',
        seaCompatibility: null // null = 전체, true = 해수, false = 담수
    });
    
    // 버튼 컨테이너 ref
    const buttonContainerRef = useRef(null);
    const tempButtonsRef = useRef(null);
    const searchInputRef = useRef(null);
    
    // � 성능 최적화: 불필요한 상태 추적 useEffect 제거하여 렌더링 최적화
    
    // ESC 키로 모달 닫기 (우선순위: ReelCompareModal > ReelModal)
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setShowCompareModal(false);
                setSelectedReel(null);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);  // 의존성 배열 비우기

    const reelTypes = useMemo(() => [
        { key: 'all', label: '전체' },
        { key: '스피닝 릴', label: '스피닝 릴' },
        { key: '로우프로파일 베이트캐스팅 릴', label: '로우프로파일 베이트캐스팅 릴' },
        { key: '클래식 베이트캐스팅 릴', label: '클래식 베이트캐스팅 릴' },
        { key: '컨벤셔널 릴', label: '컨벤셔널 릴' }
    ], []);

    // 버튼들을 균형 잡힌 줄로 배치하는 함수 (실제 너비 측정 기반)
    const createBalancedButtonRows = useCallback((buttons) => {
        if (!buttonContainerRef.current || !tempButtonsRef.current) {
            // 초기 로딩 시 기본 레이아웃 반환
            return buttons.length <= 6 ? [buttons] : [buttons.slice(0, 6), buttons.slice(6)];
        }

        const containerWidth = buttonContainerRef.current.offsetWidth;
        const availableWidth = containerWidth * 0.9; // 90% 사용
        
        // 임시로 모든 버튼을 렌더링하여 실제 너비 측정
        const tempContainer = tempButtonsRef.current;
        tempContainer.innerHTML = '';
        tempContainer.style.visibility = 'hidden';
        tempContainer.style.position = 'absolute';
        tempContainer.style.top = '-9999px';
        tempContainer.style.display = 'flex';
        tempContainer.style.gap = '10px';
        
        const buttonWidths = buttons.map((button, index) => {
            const tempButton = document.createElement('button');
            tempButton.className = 'type-button';
            tempButton.textContent = button.label;
            tempButton.style.visibility = 'hidden';
            tempContainer.appendChild(tempButton);
            const width = tempButton.offsetWidth;
            
            return { ...button, width };
        });
        
        // 정리
        tempContainer.innerHTML = '';
        
        // 자연스러운 줄바꿈으로 기본 레이아웃 생성
        const naturalLayout = [];
        let currentRow = [];
        let currentRowWidth = 0;
        
        for (const button of buttonWidths) {
            const buttonWidthWithGap = button.width + (currentRow.length > 0 ? 10 : 0);
            
            if (currentRowWidth + buttonWidthWithGap <= availableWidth || currentRow.length === 0) {
                currentRow.push(button);
                currentRowWidth += buttonWidthWithGap;
            } else {
                naturalLayout.push([...currentRow]);
                currentRow = [button];
                currentRowWidth = button.width;
            }
        }
        
        if (currentRow.length > 0) {
            naturalLayout.push(currentRow);
        }
        
        // 줄 간 버튼 수 균형 맞추기
        const balancedLayout = balanceRows(naturalLayout);
        
        // 최종 레이아웃을 원래 형태로 변환
        const result = balancedLayout.map(row => row.map(item => ({ key: item.key, label: item.label })));
        
        return result;
    }, []);

    // 줄 간 버튼 수를 균형있게 조정하는 함수
    const balanceRows = (layout) => {
        if (layout.length <= 1) {
            return layout;
        }
        
        const balanced = JSON.parse(JSON.stringify(layout)); // 깊은 복사
        
        let improved = true;
        let iterationCount = 0;
        const maxIterations = 100; // 무한 루프 방지
        
        while (improved && iterationCount < maxIterations) {
            improved = false;
            iterationCount++;
            
            for (let i = 0; i < balanced.length - 1; i++) {
                const currentRow = balanced[i];
                const nextRow = balanced[i + 1];
                const diff = currentRow.length - nextRow.length;
                
                // 차이가 2 이상인 경우 버튼 이동
                if (diff >= 2) {
                    // 현재 줄에서 마지막 버튼을 다음 줄로 이동
                    const buttonToMove = currentRow.pop();
                    nextRow.unshift(buttonToMove);
                    improved = true;
                } else if (diff <= -2) {
                    // 다음 줄에서 첫 번째 버튼을 현재 줄로 이동
                    const buttonToMove = nextRow.shift();
                    currentRow.push(buttonToMove);
                    improved = true;
                }
            }
        }
        
        return balanced;
    };

    // 컨테이너 크기 변화 감지 및 레이아웃 재계산 (최적화됨)
    useEffect(() => {
        // 성능 최적화: 필터가 열려있을 때는 레이아웃 계산 생략
        if (showFilters) {
            return;
        }
        
        // 배열이 큰 경우에도 생략
        if (reelTypes.length > 50) {
            return;
        }
        
        const calculateLayout = () => {
            if (buttonContainerRef.current) {
                const newLayout = createBalancedButtonRows(reelTypes);
                setButtonLayout(newLayout);
            }
        };

        // 초기 계산 (지연 실행)
        const timer = setTimeout(() => {
            calculateLayout();
        }, 100);

        // ResizeObserver로 크기 변화 감지
        const resizeObserver = new ResizeObserver((entries) => {
            for (let entry of entries) {
                if (entry.target === buttonContainerRef.current) {
                    calculateLayout();
                }
            }
        });

        if (buttonContainerRef.current) {
            resizeObserver.observe(buttonContainerRef.current);
        }

        // 윈도우 리사이즈 이벤트도 감지
        const handleResize = () => {
            setTimeout(calculateLayout, 50);
        };
        window.addEventListener('resize', handleResize);

        return () => {
            clearTimeout(timer);
            resizeObserver.disconnect();
            window.removeEventListener('resize', handleResize);
        };
    }, [createBalancedButtonRows, reelTypes, showFilters]);

    // 검색 입력창 크기에 따른 placeholder 동적 조정
    useEffect(() => {
        const updatePlaceholder = () => {
            if (searchInputRef.current) {
                const inputWidth = searchInputRef.current.offsetWidth;
                
                if (inputWidth < 250) {
                    setSearchPlaceholder('검색...');
                } else if (inputWidth < 350) {
                    setSearchPlaceholder('릴 이름 등 검색...');
                } else if (inputWidth < 450) {
                    setSearchPlaceholder('릴 이름, 제조사 등 검색...');
                } else {
                    setSearchPlaceholder('릴 이름, 제조사, 타입 크기 등으로 검색...');
                }
            }
        };

        // 초기 실행
        const timer = setTimeout(() => {
            updatePlaceholder();
        }, 100);

        // ResizeObserver로 검색 입력창 크기 변화 감지
        const resizeObserver = new ResizeObserver(() => {
            updatePlaceholder();
        });

        if (searchInputRef.current) {
            resizeObserver.observe(searchInputRef.current);
        }

        // 윈도우 리사이즈 이벤트 감지
        const handleResize = () => {
            setTimeout(updatePlaceholder, 50);
        };
        window.addEventListener('resize', handleResize);

        return () => {
            clearTimeout(timer);
            resizeObserver.disconnect();
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // 정렬 옵션
    const sortOptions = [
        { value: 'name', label: '이름' },
        { value: 'manufacturer', label: '제조사' },
        { value: 'max_drag', label: '최대 저항' },
        { value: 'price_silver', label: '가격 (실버)' }
    ];

    // 필터 옵션을 지연 로딩하는 함수
    const loadFilterOptions = useCallback(async () => {
        if (filterState.loaded || filterState.loading) {
            return;
        }
        
        setFilterState(prev => ({ ...prev, loading: true }));
        
        try {
            const manufacturersPromise = fetch('/api/reel-manufacturers');
            const sizesPromise = fetch('/api/reel-sizes-list');
            
            // 병렬 요청 실행
            const [manufacturersRes, sizesRes] = await Promise.all([
                manufacturersPromise,
                sizesPromise
            ]);
            
            // 데이터 수집 및 파싱
            let manufacturersDataForBatch = null;
            let sizesDataForBatch = null;
            
            if (manufacturersRes.ok) {
                manufacturersDataForBatch = await manufacturersRes.json();
            } else {
                console.error('❌ [Filter] 제조사 API 요청 실패:', manufacturersRes.status);
            }
            
            if (sizesRes.ok) {
                sizesDataForBatch = await sizesRes.json();
            } else {
                console.error('❌ [Filter] 사이즈 API 요청 실패:', sizesRes.status);
            }
            
            // 통합된 단일 상태 업데이트로 렌더링 최적화
            setFilterState({
                manufacturers: manufacturersDataForBatch || [],
                sizes: sizesDataForBatch || [],
                loaded: true,
                loading: false
            });
            
        } catch (error) {
            console.error('❌ [Filter] 필터 옵션 로드 실패:', error);
        } finally {
            setFilterState(prev => ({ ...prev, loading: false }));
        }
    }, [filterState.loaded, filterState.loading]);

    // 필터 메뉴 토글 (필터 옵션 지연 로딩)
    const toggleFilters = useCallback(async () => {
        if (!showFilters && !filterState.loaded) {
            await loadFilterOptions();
        }
        
        setShowFilters(prev => !prev);
    }, [showFilters, filterState.loaded, loadFilterOptions]);

    useEffect(() => {
        const fetchReels = async () => {
            setLoading(true);
            try {
                let url = '/api/reels';
                
                // 필터가 적용된 경우 필터된 API 사용
                const hasFilters = filters.manufacturer !== 'all' || 
                                 filters.size !== 'all' || 
                                 filters.seaCompatibility !== null;
                
                if (hasFilters) {
                    url = '/api/reels-filtered';
                    const params = new URLSearchParams();
                    if (filters.manufacturer !== 'all') params.set('manufacturer', filters.manufacturer);
                    if (filters.size !== 'all') params.set('size', filters.size);
                    if (filters.seaCompatibility !== null) {
                        params.set('seaCompatibility', filters.seaCompatibility.toString());
                    }
                    // 릴 타입 필터도 추가
                    if (selectedType !== 'all') {
                        params.set('reelType', selectedType);
                    }
                    url += `?${params.toString()}`;
                } else if (selectedType !== 'all') {
                    url = `/api/reels/${encodeURIComponent(selectedType)}`;
                }
                
                const res = await fetch(url);
                if (!res.ok) throw new Error('데이터를 불러올 수 없습니다');
                
                const data = await res.json();
                setReels(data);
                
            } catch (e) {
                console.error('❌ [Data] Error fetching reels:', e);
                setReels([]);
            }
            
            setLoading(false);
        };
        
        const fetchReelSizes = async () => {
            try {
                let url = '/api/reel-sizes';
                
                // 필터가 적용된 경우 필터된 API 사용
                const hasFilters = filters.manufacturer !== 'all' || 
                                 filters.size !== 'all' || 
                                 filters.seaCompatibility !== null;
                
                if (hasFilters || selectedType !== 'all') {
                    const params = new URLSearchParams();
                    if (filters.manufacturer !== 'all') params.set('manufacturer', filters.manufacturer);
                    if (filters.size !== 'all') params.set('size', filters.size);
                    if (filters.seaCompatibility !== null) {
                        params.set('seaCompatibility', filters.seaCompatibility.toString());
                    }
                    // 릴 타입 필터도 추가
                    if (selectedType !== 'all') {
                        params.set('reelType', selectedType);
                    }
                    url += `?${params.toString()}`;
                }
                
                const res = await fetch(url);
                if (!res.ok) throw new Error('릴 크기 데이터를 불러올 수 없습니다');
                
                const data = await res.json();
                setReelSizes(data);
                
            } catch (e) {
                console.error('❌ [Sizes] Error fetching reel sizes:', e);
                setReelSizes([]);
            }
        };
        
        Promise.all([fetchReels(), fetchReelSizes()]);
    }, [selectedType, filters]);

    // 검색어에 따른 필터링과 정렬 - useMemo로 최적화
    const filteredReels = useMemo(() => {
        let filtered = reels;

        // 검색어 필터링
        if (searchTerm.trim()) {
            filtered = filtered.filter(reel => 
                reel.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                reel.manufacturer.toLowerCase().includes(searchTerm.toLowerCase()) ||
                reel.reel_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (reel.size_names && reel.size_names.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }

        // 정렬
        const sorted = [...filtered].sort((a, b) => {
            switch (sortBy) {
                case 'name':
                    return a.name.localeCompare(b.name);
                case 'manufacturer':
                    return a.manufacturer.localeCompare(b.manufacturer);
                case 'max_drag':
                    return (b.max_drag || 0) - (a.max_drag || 0);
                case 'price_silver':
                    return (b.price_silver || 0) - (a.price_silver || 0);
                default:
                    return 0;
            }
        });
        
        return sorted;
    }, [searchTerm, reels, sortBy]);

    // 🚀 ReelTableView props 메모화 (성능 최적화) - 필터 패널 상태 독립
    const memoizedTableProps = useMemo(() => {
        return {
            reels: filteredReels,
            reelSizes: reelSizes,
            searchTerm: searchTerm
        };
    }, [filteredReels, reelSizes, searchTerm]);

    const handleReelClick = useCallback(async (reelId) => {
        setModalLoading(true);
    try {
      const res = await fetch(`/api/reels/detail/${reelId}`);
            if (!res.ok) throw new Error('상세 정보를 불러올 수 없습니다');
            const data = await res.json();
            setSelectedReel(data);
        } catch (e) {
            console.error('Error fetching reel detail:', e);
            alert('릴 상세 정보를 불러오는데 실패했습니다.');
        }
        setModalLoading(false);
    }, []);

    // 비교 목록에 크기별 릴 추가
    const handleAddToCompare = useCallback((reel, size) => {
        setCompareList(prev => {
            const exists = prev.find(item => item.reelId === reel.id && item.sizeName === size.name);
            if (exists) {
                return prev; // 이미 있으면 그대로
            }
            
            // 최대 4개까지만 허용
            if (prev.length >= 4) {
                alert('최대 4개까지만 비교할 수 있습니다.');
                return prev;
            }
            
            return [...prev, {
                reelId: reel.id,
                sizeName: size.name,
                reel: reel,
                size: size
            }];
        });
    }, []);

    // 비교 목록에서 특정 크기의 릴 제거
    const handleRemoveFromCompare = useCallback((reelId, sizeName) => {
        setCompareList(prev => prev.filter(item => !(item.reelId === reelId && item.sizeName === sizeName)));
    }, []);

    // 모달 닫기 핸들러들
    const handleCloseReelModal = useCallback(() => {
        setSelectedReel(null);
    }, []);

    const handleCloseCompareModal = useCallback(() => {
        setShowCompareModal(false);
    }, []);

    // 비교 모달 열기
    const handleCompareClick = useCallback(() => {
        if (compareList.length < 2) {
            alert('비교하려면 최소 2개의 릴을 선택해주세요.');
            return;
        }
        setShowCompareModal(true);
    }, [compareList.length]);

    // 비교 목록 초기화
    const handleClearCompare = useCallback(() => {
        setCompareList([]);
    }, []);

    // 뷰 모드 변경 및 로컬 스토리지 저장
    const handleViewModeChange = (newViewMode) => {
        setViewMode(newViewMode);
        try {
            localStorage.setItem('reelsViewMode', newViewMode);
        } catch (error) {
            console.warn('로컬 스토리지에 뷰 모드를 저장하는데 실패했습니다:', error);
        }
    };
    
    // 필터 변경 핸들러
    const handleFilterChange = (filterType, value) => {
        setFilters(prev => ({
            ...prev,
            [filterType]: value
        }));
    };
    
    // 필터 초기화
    const resetFilters = () => {
        setFilters({
            manufacturer: 'all',
            size: 'all',
            seaCompatibility: null
        });
    };

    return (
        <>
            <h1 className="page-title">릴 정보</h1>
            {/* 검색 기능과 정렬 */}
            <div className="search-and-sort-container">
                <div className="search-container" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    maxWidth: '500px',
                    margin: '0 auto'
                }}>
                    <input 
                        ref={searchInputRef}
                        type="text"
                        placeholder={searchPlaceholder}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                    {searchTerm && (
                        <button 
                            onClick={() => setSearchTerm('')}
                            className="search-clear-btn"
                        >
                            ×
                        </button>
                    )}
                </div>
                
                {/* 정렬 선택 */}
                <div className="sort-container">
                    <label htmlFor="sort-select">정렬:</label>
                    <select 
                        id="sort-select"
                        value={sortBy} 
                        onChange={(e) => setSortBy(e.target.value)}
                        className="sort-select"
                    >
                        {sortOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
                
                {/* 뷰 모드 전환 버튼과 필터 버튼 */}
                <div className="view-mode-container">
                    <label>보기:</label>
                    <div className="view-mode-buttons">
                        <button 
                            className={`view-mode-btn ${viewMode ===
                                 'grid' ? 'active' : ''}`}
                            onClick={() => handleViewModeChange('grid')}
                            title="그리드 보기"
                        >
                            ⊞
                        </button>
                        <button 
                            className={`view-mode-btn ${viewMode === 'table' ? 'active' : ''}`}
                            onClick={() => handleViewModeChange('table')}
                            title="테이블 보기"
                        >
                            ≡
                        </button>
                        <button 
                            className={`view-mode-btn ${showFilters ? 'active' : ''}`}
                            onClick={toggleFilters}
                            title="필터 열기/닫기"
                            disabled={filterState.loading}
                        >
                            {filterState.loading ? '...' : '필터'}
                        </button>
                    </div>
                </div>
            </div>

            {/* 필터 패널 - 최적화된 조건부 렌더링 */}
            {showFilters && (
                <div className="filter-panel" style={{
                    backgroundColor: 'var(--filter-bg, #f8f9fa)',
                    padding: '20px',
                    borderRadius: '8px',
                    margin: '15px 40px',
                    border: '1px solid var(--filter-border, #e9ecef)',
                    color: 'var(--filter-text, #212529)',
                    boxShadow: 'var(--filter-shadow, 0 2px 4px rgba(0,0,0,0.1))',
                    // 🚀 하드웨어 가속으로 CSS 성능 최적화
                    transform: 'translate3d(0, 0, 0)',
                    willChange: 'auto',
                    backfaceVisibility: 'hidden'
                }}>
                    
                    {/* 로딩 상태를 먼저 체크 */}
                    {filterState.loading ? (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                            <div>로딩 중...</div>
                        </div>
                    ) : (
                        <div className="filter-content" style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'center',
                            gap: '20px',
                            alignItems: 'center'
                        }}>
                            {/* 제조사 필터 */}
                            <div className="filter-group" style={{ minWidth: 'auto', width: 'auto' }}>
                                <label className="filter-label" style={{ 
                                    display: 'block', 
                                    marginBottom: '8px', 
                                    fontWeight: 'bold',
                                    color: 'var(--filter-label-color, #495057)'
                                }}>
                                    제조사
                                </label>
                                <select
                                    value={filters.manufacturer}
                                    onChange={(e) => handleFilterChange('manufacturer', e.target.value)}
                                    style={{
                                        width: 'auto',
                                        minWidth: '120px',
                                        padding: '8px 12px',
                                        border: '1px solid var(--filter-input-border, #ced4da)',
                                        borderRadius: '4px',
                                        backgroundColor: 'var(--filter-input-bg, white)',
                                        color: 'var(--filter-input-text, #495057)',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    {filterState.manufacturers.map(manufacturer => (
                                        <option key={manufacturer} value={manufacturer}>
                                            {manufacturer === 'all' ? '전체' : manufacturer}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* 사이즈 필터 */}
                            <div className="filter-group" style={{ minWidth: 'auto', width: 'auto' }}>
                                <label className="filter-label" style={{ 
                                    display: 'block', 
                                    marginBottom: '8px', 
                                    fontWeight: 'bold',
                                    color: 'var(--filter-label-color, #495057)'
                                }}>
                                    사이즈
                                </label>
                                <select
                                    value={filters.size}
                                    onChange={(e) => handleFilterChange('size', e.target.value)}
                                    style={{
                                        width: 'auto',
                                        minWidth: '120px',
                                        padding: '8px 12px',
                                        border: '1px solid var(--filter-input-border, #ced4da)',
                                        borderRadius: '4px',
                                        backgroundColor: 'var(--filter-input-bg, white)',
                                        color: 'var(--filter-input-text, #495057)',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    {filterState.sizes.map(size => (
                                        <option key={size} value={size}>
                                            {size === 'all' ? '전체' : size}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* 해수저항 필터 */}
                            <div className="filter-group" style={{ minWidth: 'max-content', width: 'auto' }}>
                                <label className="filter-label" style={{ 
                                    display: 'block', 
                                    marginBottom: '8px', 
                                    fontWeight: 'bold',
                                    color: 'var(--filter-label-color, #495057ff)',
                                    whiteSpace: 'nowrap'
                                }}>
                                    해수저항
                                </label>
                                <div style={{ 
                                    display: 'flex', 
                                    gap: '15px', 
                                    alignItems: 'center', 
                                    height: '36px',
                                    whiteSpace: 'nowrap',
                                    minWidth: 'max-content'
                                }}>
                                    <ToggleSwitch
                                        checked={filters.seaCompatibility === true}
                                        onChange={() => handleFilterChange('seaCompatibility', filters.seaCompatibility === true ? null : true)}
                                        label="해수 저항만 표시"
                                    />
                                </div>
                            </div>

                            {/* 필터 초기화 버튼 */}
                            <div className="filter-group" style={{ display: 'flex', alignItems: 'end', minWidth: 'auto', width: 'auto' }}>
                                <button
                                    onClick={resetFilters}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: 'var(--filter-button-bg, #007bff)',
                                        color: 'var(--filter-button-text, white)',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onMouseOver={(e) => e.target.style.backgroundColor = 'var(--filter-button-hover, #0056b3)'}
                                    onMouseOut={(e) => e.target.style.backgroundColor = 'var(--filter-button-bg, #007bff)'}
                                >
                                    필터 초기화
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* 릴 타입 선택 버튼 */}
            <div className="reel-type-buttons-container" ref={buttonContainerRef}>
                {/* 임시 버튼들을 위한 숨겨진 컨테이너 */}
                <div ref={tempButtonsRef} style={{ visibility: 'hidden', position: 'absolute', top: '-9999px' }}></div>
                
                {buttonLayout.map((row, rowIndex) => (
                    <div key={rowIndex} className="reel-type-buttons-row">
                        {row.map(type => (
                            <button 
                                key={type.key}
                                className={`type-button ${selectedType === type.key ? 'active' : ''}`}
                                onClick={() => setSelectedType(type.key)}
                            >
                                {type.label}
                            </button>
                        ))}
                    </div>
                ))}
            </div>
            <div className="page-content" style={{
                transform: 'translate3d(0, 0, 0)',
                willChange: 'auto'
            }}>
                
                {loading ? (
                    <div className="spinner-container">
                        <LoadingSpinner height={80} width={80} color="#4fa94d" visible={true} />
                    </div>
                ) : (
                    <>
                        {/* 검색 결과 개수 표시 */}
                        {searchTerm && (
                            <div className="search-results-info">
                                <p>'{searchTerm}' 검색 결과: {filteredReels.length}개</p>
                            </div>
                        )}
                        
                        {filteredReels.length === 0 ? (
                            <div className="no-data">
                                <p>{searchTerm ? '검색 결과가 없습니다.' : '해당 타입의 릴 데이터가 없습니다.'}</p>
                            </div>
                        ) : (
                            <>
                                {/* 뷰 모드에 따른 렌더링 */}
                                {viewMode === 'table' ? (
                                    <div style={{ 
                                        transform: 'translate3d(0, 0, 0)', 
                                        willChange: 'auto',
                                        backfaceVisibility: 'hidden'
                                    }}>
                                        <ReelTableView 
                                            {...memoizedTableProps}
                                        />
                                    </div>
                                ) : (
                                    <>
                                        <div className="reel-grid">
                                            {filteredReels.map((reel) => (
                                                <ReelTile 
                                                    key={reel.id}
                                                    reel={reel}
                                                    onReelClick={handleReelClick}
                                                />
                                            ))}
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
            
            {modalLoading && (
                <div className="modal-overlay">
                    <LoadingSpinner height={80} width={80} color="#4fa94d" visible={true} />
                </div>
            )}
            
            {selectedReel && (
                <ReelModal 
                    reel={selectedReel} 
                    onClose={handleCloseReelModal}
                    onAddToCompare={handleAddToCompare}
                    onRemoveFromCompare={handleRemoveFromCompare}
                    compareList={compareList}
                />
            )}
            
            {/* 비교 기능 UI */}
            {compareList.length > 0 && (
                <div className="compare-controls">
                    <div className="compare-info">
                        <span>선택된 릴: {compareList.length}개</span>
                        <div className="compare-list">
                            {compareList.map(item => (
                                <span key={`${item.reelId}-${item.sizeName}`} className="compare-item">
                                    {item.reel.name} {item.sizeName}
                                </span>
                            ))}
                        </div>
                    </div>
                    <div className="compare-buttons">
                        <button 
                            className="compare-btn primary"
                            onClick={handleCompareClick}
                            disabled={compareList.length < 2}
                        >
                            비교하기 ({compareList.length})
                        </button>
                        <button 
                            className="compare-btn secondary"
                            onClick={handleClearCompare}
                        >
                            선택 초기화
                        </button>
                    </div>
                </div>
            )}
            
            {/* 비교 모달 */}
            {showCompareModal && (
                <ReelCompareModal 
                    compareItems={compareList}
                    onClose={handleCloseCompareModal}
                    onRemoveFromCompare={handleRemoveFromCompare}
                />
            )}
            
            <Comments pageName="reels" />
        </>
    );
};

export default Page1;
